/**
 * Canvas theme_config consumption (spec section 14.2).
 *
 * A canvas may carry a `theme_config` object. This module validates it against
 * the frozen whitelist, resolves each whitelisted key to a CSS-variable value,
 * and produces the flat variable record that `CanvasThemeScope` injects scoped
 * to the canvas root. It is pure: no React, no DOM.
 *
 * Contract, verbatim from spec 14.2:
 *  - Whitelisted keys only; unknown keys are ignored (item 1).
 *      palette.primary | palette.neutral | palette.background | palette.surface
 *      | palette.text  -- each a token reference or hex literal
 *      density  -- "comfortable" | "compact"  (default "comfortable")
 *      radius   -- "none" | "sm" | "md" | "lg" (default "md")
 *  - Value grammar for a palette entry (item 1):
 *      token reference: ^token:(primary|neutral|success|warning|danger)\.(50|100|
 *                       200|300|400|500|600|700|800|900)$
 *      hex literal:     ^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$
 *    Any other string is invalid.
 *  - Light/dark (item 1): one value is scheme-agnostic. A hex literal is emitted
 *    as the same value under both schemes; a `token:` reference is emitted as
 *    `var(--joy-palette-<channel>-<step>)`, and that referenced variable already
 *    carries distinct light/dark values from the brand/org layers -- so a single
 *    scoped block tracks both schemes and no per-scheme duplication is needed.
 *  - Precedence brand -> org -> canvas (item 2) is achieved by DOM SCOPING, not a
 *    JS merge: brand is the base theme, org variables are set on `html`, and the
 *    canvas variables here are set on the canvas-root element. A CSS custom
 *    property takes its value from the nearest ancestor that sets it, so within
 *    the canvas subtree the canvas value wins for exactly the keys it sets, and
 *    unset keys inherit org/brand. This module therefore emits variables only for
 *    validly-set canvas keys; it never emits a variable it would have to inherit.
 *  - Invalid-value handling (item 3): an invalid hex or unknown token for a
 *    whitelisted key is dropped (so the org/brand value inherits) and logged. The
 *    canvas never fails to render due to a bad theme_config.
 *
 * IMPLEMENTATION DECISION (not pinned by spec 14.2 -- flagged as a slice note):
 * spec 14.2 names the whitelisted keys and says palette values use the
 * `--joy-palette-*` mechanism, but does not enumerate the exact target variable
 * per semantic key. The mapping below is the minimal faithful choice -- each
 * semantic key overrides the single Joy semantic variable that most directly
 * carries it. `density` and `radius` have no Joy semantic variable, so they are
 * emitted as canvas-scoped custom properties (`--canvas-density`, `--canvas-radius`)
 * for the canvas components to consume; `radius` maps onto the Joy radius scale
 * as spec 14.2 states ("maps to the Joy radius scale").
 */

// ---------------------------------------------------------------------------
// Whitelist (spec 14.2 item 1)
// ---------------------------------------------------------------------------

export const PALETTE_KEYS = [
  "primary",
  "neutral",
  "background",
  "surface",
  "text",
] as const;
export type PaletteKey = (typeof PALETTE_KEYS)[number];

export const DENSITY_VALUES = ["comfortable", "compact"] as const;
export type DensityValue = (typeof DENSITY_VALUES)[number];
export const DEFAULT_DENSITY: DensityValue = "comfortable";

export const RADIUS_VALUES = ["none", "sm", "md", "lg"] as const;
export type RadiusValue = (typeof RADIUS_VALUES)[number];
export const DEFAULT_RADIUS: RadiusValue = "md";

/** Value grammar (spec 14.2 item 1), transcribed exactly. */
export const TOKEN_REF_RE =
  /^token:(primary|neutral|success|warning|danger)\.(50|100|200|300|400|500|600|700|800|900)$/;
export const HEX_RE = /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/;

/**
 * Target Joy semantic CSS variable for each whitelisted palette key.
 * Implementation decision (see file header) -- not pinned by spec 14.2.
 */
export const PALETTE_TARGET_VAR: Record<PaletteKey, string> = {
  primary: "--joy-palette-primary-500",
  neutral: "--joy-palette-neutral-500",
  background: "--joy-palette-background-body",
  surface: "--joy-palette-background-surface",
  text: "--joy-palette-text-primary",
};

/** Canvas-scoped custom property names for the non-palette keys. */
export const DENSITY_VAR = "--canvas-density";
export const RADIUS_VAR = "--canvas-radius";

/**
 * Numeric density multiplier the canvas grid multiplies its gap and row height
 * by (`calc(<base> * var(--canvas-density-scale, 1))`). Separate from the enum
 * `--canvas-density` (which is informational) because CSS cannot do arithmetic
 * on the word "compact". Consumers fall back to 1 when the variable is unset, so
 * a canvas with no density override keeps its current spacing.
 */
export const DENSITY_SCALE_VAR = "--canvas-density-scale";

/**
 * density enum -> numeric scale. comfortable is the identity (1); compact packs
 * spacing to 0.75 of comfortable. Implementation decision (see file header) --
 * spec 14.2 names the density enum but not a numeric factor.
 */
export const DENSITY_SCALE: Record<DensityValue, string> = {
  comfortable: "1",
  compact: "0.75",
};

/** radius enum -> Joy radius scale value (spec 14.2: "maps to the Joy radius scale"). */
export const RADIUS_CSS: Record<RadiusValue, string> = {
  none: "0px",
  sm: "var(--joy-radius-sm)",
  md: "var(--joy-radius-md)",
  lg: "var(--joy-radius-lg)",
};

// ---------------------------------------------------------------------------
// Value resolution
// ---------------------------------------------------------------------------

/**
 * Resolve one palette value string to a CSS value, or `null` if invalid.
 *  - token reference `token:<channel>.<step>` -> `var(--joy-palette-<channel>-<step>)`
 *  - hex literal -> the literal, unchanged (scheme-agnostic)
 */
export function resolvePaletteValue(raw: unknown): string | null {
  if (typeof raw !== "string") {
    return null;
  }
  const tokenMatch = TOKEN_REF_RE.exec(raw);
  if (tokenMatch) {
    const [, channel, step] = tokenMatch;
    return `var(--joy-palette-${channel}-${step})`;
  }
  if (HEX_RE.test(raw)) {
    return raw;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Normalization
// ---------------------------------------------------------------------------

/** A normalized canvas theme: only validly-set palette keys, plus resolved enums. */
export interface NormalizedCanvasTheme {
  /** Resolved CSS value per validly-set palette key. Absent keys inherit org/brand. */
  palette: Partial<Record<PaletteKey, string>>;
  density: DensityValue;
  radius: RadiusValue;
}

export interface NormalizeResult {
  theme: NormalizedCanvasTheme;
  /** Human-readable notes for each dropped/invalid value (logged by the caller). */
  warnings: string[];
}

export interface NormalizeOptions {
  /**
   * The canvas-level `color` accent (the `String(50)` shorthand). Used only as
   * the fallback for an ABSENT `palette.primary` (spec 14.2 item 1: "falls back
   * to color, then org/brand primary"). An INVALID `palette.primary` falls back
   * to org/brand (item 3), not to color.
   */
  color?: string;
  /** Sink for fallback logs; defaults to `console.warn`. */
  logger?: (message: string) => void;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/**
 * Validate and normalize a raw `theme_config` against the spec 14.2 whitelist.
 * Unknown keys are ignored; invalid values are dropped and recorded as warnings
 * so the org/brand value inherits. Never throws.
 */
export function normalizeCanvasThemeConfig(
  raw: unknown,
  opts: NormalizeOptions = {},
): NormalizeResult {
  const warnings: string[] = [];
  const palette: Partial<Record<PaletteKey, string>> = {};

  const rawObj = isPlainObject(raw) ? raw : {};
  const rawPalette = isPlainObject(rawObj.palette) ? rawObj.palette : {};

  for (const key of PALETTE_KEYS) {
    const present = Object.prototype.hasOwnProperty.call(rawPalette, key);
    if (present) {
      const resolved = resolvePaletteValue(rawPalette[key]);
      if (resolved !== null) {
        palette[key] = resolved;
      } else {
        warnings.push(
          `theme_config.palette.${key}: invalid value ` +
            `${JSON.stringify(rawPalette[key])}; falling back to org/brand.`,
        );
      }
    } else if (key === "primary" && opts.color !== undefined) {
      // Absent primary falls back to the canvas `color` accent when that is a
      // valid palette value; otherwise it inherits org/brand (no variable).
      const fromColor = resolvePaletteValue(opts.color);
      if (fromColor !== null) {
        palette.primary = fromColor;
      }
    }
  }

  const density = normalizeEnum(
    rawObj.density,
    DENSITY_VALUES,
    DEFAULT_DENSITY,
    "density",
    warnings,
  );
  const radius = normalizeEnum(
    rawObj.radius,
    RADIUS_VALUES,
    DEFAULT_RADIUS,
    "radius",
    warnings,
  );

  const logger = opts.logger ?? ((m: string) => console.warn(`[canvas-theme] ${m}`));
  for (const w of warnings) {
    logger(w);
  }

  return { theme: { palette, density, radius }, warnings };
}

function normalizeEnum<T extends string>(
  raw: unknown,
  allowed: readonly T[],
  fallback: T,
  keyName: string,
  warnings: string[],
): T {
  if (raw === undefined) {
    return fallback;
  }
  if (typeof raw === "string" && (allowed as readonly string[]).includes(raw)) {
    return raw as T;
  }
  warnings.push(
    `theme_config.${keyName}: invalid value ${JSON.stringify(raw)}; ` +
      `using default "${fallback}".`,
  );
  return fallback;
}

// ---------------------------------------------------------------------------
// CSS variable emission
// ---------------------------------------------------------------------------

/**
 * Build the flat CSS-variable record for a normalized canvas theme. Emits a
 * variable only for validly-set palette keys (so unset keys inherit org/brand),
 * and emits the density and radius variables ONLY when they differ from the
 * default. A canvas that sets neither density nor radius (nor any palette key)
 * therefore produces an empty record, and the canvas consumers fall back --
 * via `var(--canvas-density-scale, 1)` and `var(--canvas-radius, var(--joy-radius-md))`
 * -- to exactly the spacing and radius they used before this slice, so no-config
 * rendering is byte-identical (spec 14.2 item 2 / item 7). A non-default density
 * emits BOTH the informational enum (`--canvas-density`) and the numeric factor
 * (`--canvas-density-scale`) the grid multiplies its gap and row height by.
 * This record is set on the canvas-root element by `CanvasThemeScope`.
 */
export function buildCanvasCssVars(theme: NormalizedCanvasTheme): Record<string, string> {
  const vars: Record<string, string> = {};
  for (const key of PALETTE_KEYS) {
    const value = theme.palette[key];
    if (value !== undefined) {
      vars[PALETTE_TARGET_VAR[key]] = value;
    }
  }
  if (theme.density !== DEFAULT_DENSITY) {
    vars[DENSITY_VAR] = theme.density;
    vars[DENSITY_SCALE_VAR] = DENSITY_SCALE[theme.density];
  }
  if (theme.radius !== DEFAULT_RADIUS) {
    vars[RADIUS_VAR] = RADIUS_CSS[theme.radius];
  }
  return vars;
}

/**
 * Convenience: validate a raw theme_config and produce its CSS variables in one
 * call. A `null`/absent/empty config yields an EMPTY record: no palette override,
 * and no density/radius variable because both are at their default (spec 14.2
 * item 2 -- a canvas with no theme_config renders identical to today; the canvas
 * consumers' CSS fallbacks supply the same comfortable/md they already used).
 */
export function canvasThemeVars(
  raw: unknown,
  opts: NormalizeOptions = {},
): Record<string, string> {
  return buildCanvasCssVars(normalizeCanvasThemeConfig(raw, opts).theme);
}
